import Button from "@components/blocks/Button";
import NavBarLogo from "@components/common/NavBar/NavBarLogo";
import SimpleRichText from "@components/common/SimpleRichText";
import StrapiImage from "@components/common/StrapiImage";
import { Title } from "@justhome/react-ui";
import useGlobalContext from "@lib/hooks/useGlobalContext";
import usePartnerContext from "@lib/hooks/usePartnerContext";
import { BudgetCheckStepId } from "@modules/application/lib/budgetCheck/types";
import { memo } from "react";
import { FieldValues } from "../DynamicForm";
import { FormStep, NextStep } from "../FormSteps/FormSteps";
import ProcessSteps from "../ProcessSteps";

interface ApplicationStepsProps<T extends FieldValues> {
  step: FormStep<T>;
  value: any;
  state: number;
  steps: NextStep[];
  onPrevious: (stepId?: string) => void;
}

const AsideQuestion = memo(function AsideQuestion() {
  const context = useGlobalContext();
  const applicationData = context.siteData?.applicationProcess;
  const openQuestion = applicationData?.data?.attributes?.openQuestion;
  if (!openQuestion) {
    return null;
  }
  return (
    <div className="mt-auto px-m py-3s">
      <div className="flex gap">
        <div className="flex-1">
          <Title as="h3" className="text-h3 font-normal">
            {openQuestion.title}
          </Title>
          <SimpleRichText size="small" className="text-body-2 mb-3xs mt-xs">
            {openQuestion.description}
          </SimpleRichText>
        </div>
        <div className="flex-none">
          <div className="rounded-full border border-neutral-50 flex overflow-hidden bg-neutral-50">
            <StrapiImage
              alt=""
              width="80"
              height="80"
              image={openQuestion.image}
            />
          </div>
        </div>
      </div>
      <div className="flex gap-2s">
        {openQuestion.buttons &&
          openQuestion.buttons.map(
            (button, index) =>
              button && (
                <Button
                  {...button}
                  key={index}
                  variant="outline_primary"
                  iconColor="currentColor"
                />
              ),
          )}
        {openQuestion.button && <Button {...openQuestion.button} />}
      </div>
    </div>
  );
});

const ApplicationSteps = <T extends FieldValues>({
  steps,
  step,
  onPrevious,
}: ApplicationStepsProps<T>) => {
  const { partner } = usePartnerContext();
  return (
    <div className="bg-neutral-12  w-full flex flex-col">
      <div className="px-s m:px-m py-3xs">
        <NavBarLogo href="/" className="mb-3m" partner={partner} />
        <ProcessSteps>
          {steps.map((s, index) => {
            if (
              [BudgetCheckStepId.loading, BudgetCheckStepId.contact].includes(
                s.id as BudgetCheckStepId,
              )
            ) {
              return null;
            }
            return (
              <ProcessSteps.Step
                onPreviousStep={() => onPrevious(s.id)}
                index={index}
                key={s.label}
                label={s.label || ""}
                completed={!!s.completed || !!step.isResult}
                selected={step.id === s.id}
              />
            );
          })}
        </ProcessSteps>
      </div>
      <AsideQuestion />
    </div>
  );
};

export default ApplicationSteps;
